import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { X, Info, AlertTriangle, CheckCircle2, XCircle, Megaphone, Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../atoms/button';

const bannerVariants = cva(
  'relative flex w-full items-start gap-3 rounded-[0.375rem] border px-4 py-3 text-sm',
  {
    variants: {
      tone: {
        informational: 'border-border-info bg-info-subtle text-foreground-info',
        promotional: 'border-border-brand bg-brand-subtle text-foreground-brand',
        warning: 'border-border-warning bg-warning-subtle text-foreground-warning',
        error: 'border-border-danger bg-danger-subtle text-foreground-danger',
        success: 'border-border-success bg-success-subtle text-foreground-success',
        announcement: 'border-border bg-surface-raised text-foreground',
      },
    },
    defaultVariants: { tone: 'informational' },
  },
);

const bannerIcon = {
  informational: Info,
  promotional: Sparkles,
  warning: AlertTriangle,
  error: XCircle,
  success: CheckCircle2,
  announcement: Megaphone,
} as const;

export interface BannerProps extends VariantProps<typeof bannerVariants> {
  className?: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode | false;
  action?: React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  children?: React.ReactNode;
  /** Auto-dismiss after 5s, like a toast. Set false for banners the user must read (e.g. a one-time secret notice). Defaults to true. */
  autoHide?: boolean;
}

const AUTO_HIDE_MS = 5000;

const Banner = React.forwardRef<HTMLDivElement, BannerProps>(
  (
    {
      className,
      tone = 'informational',
      title,
      description,
      icon,
      action,
      dismissible,
      onDismiss,
      children,
      autoHide = true,
    },
    ref,
  ) => {
    const Icon = bannerIcon[tone ?? 'informational'];
    const [hidden, setHidden] = React.useState(false);
    const onDismissRef = React.useRef(onDismiss);
    onDismissRef.current = onDismiss;

    React.useEffect(() => {
      setHidden(false);
      if (!autoHide) {
        return;
      }
      const id = window.setTimeout(() => {
        setHidden(true);
        onDismissRef.current?.();
      }, AUTO_HIDE_MS);
      return () => window.clearTimeout(id);
    }, [title, description, children, autoHide]);

    function renderIcon() {
      if (icon === false) return null;
      if (icon) {
        return <span className="shrink-0 [&_svg]:size-5">{icon}</span>;
      }
      return <Icon className="size-5 shrink-0" aria-hidden />;
    }

    if (hidden) {
      return null;
    }

    return (
      <div
        ref={ref}
        role="region"
        className={cn(bannerVariants({ tone }), 'relative z-10 items-center', className)}
      >
        {renderIcon()}
        <div className="min-w-0 flex-1 space-y-1">
          {title && <div className="font-medium text-foreground">{title}</div>}
          {description && <div className="text-muted-foreground">{description}</div>}
          {children}
          {action && <div className="pt-2">{action}</div>}
        </div>
        {dismissible && (
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label="Dismiss banner"
            onClick={onDismiss}
          >
            <X />
          </Button>
        )}
      </div>
    );
  },
);
Banner.displayName = 'Banner';

export { Banner, bannerVariants };
